import type { Editor } from '@tiptap/react';

import { updateTaskItemAttrs } from './taskListItemAttrUtils';
import type { TaskItemDueDate } from './taskListItemChipTypes';

/** Reads the `dueDate` attr off a task item node's attrs, defaulting to `null`. */
export const getTaskItemDueDate = (attrs: Record<string, unknown> | undefined): TaskItemDueDate | null =>
    (attrs?.dueDate as TaskItemDueDate | null | undefined) ?? null;

/** Reads the `showDueDate` attr off a task item node's attrs, defaulting to `false`. */
export const getTaskItemShowDueDate = (attrs: Record<string, unknown> | undefined): boolean =>
    Boolean(attrs?.showDueDate);

/**
 * Sets a due date on the task item node at `position`. Also sets `showDueDate`
 * so the chip stays visible (mirrors the element `setElementDueDate`).
 */
export const setTaskItemDueDate = ({
    editor,
    position,
    dueDate,
}: {
    editor: Editor;
    position: number;
    dueDate: TaskItemDueDate;
}): void => {
    updateTaskItemAttrs({ editor, position, update: () => ({ dueDate, showDueDate: true }) });
};

/**
 * Clears the due date on the task item node at `position`. `showDueDate` keeps
 * the "Set due date" placeholder chip visible after clearing (e.g. re-picking
 * the selected calendar day removes the date but leaves the popup open),
 * mirroring the element `removeElementDueDate`.
 */
export const clearTaskItemDueDate = ({
    editor,
    position,
    showDueDate = false,
}: {
    editor: Editor;
    position: number;
    showDueDate?: boolean;
}): void => {
    updateTaskItemAttrs({ editor, position, update: () => ({ dueDate: null, showDueDate }) });
};

/**
 * Toggles the "Set due date" placeholder chip on the task item node at `position`,
 * clearing any set due date either way (mirrors the element
 * `toggleElementShowDueDatePill`). Returns the new `showDueDate` value, or
 * `undefined` when there is no node at `position`.
 */
export const toggleTaskItemShowDueDate = ({
    editor,
    position,
}: {
    editor: Editor;
    position: number;
}): boolean | undefined => {
    const attrs = updateTaskItemAttrs({
        editor,
        position,
        update: (currentAttrs) => ({ dueDate: null, showDueDate: !getTaskItemShowDueDate(currentAttrs) }),
    });

    return attrs && getTaskItemShowDueDate(attrs);
};
